/**
 * ========================
 * COMMUNICATIONS · WHATSAPP MENU PROVIDER
 * ========================
 *
 * Menu for the WhatsApp channel sub-app (mounted at
 * `/admin/communications/whatsapp/*`). Reached from the Communications hub;
 * `comms-whatsapp` is a `hidden` domain so it never shows in the global switcher.
 *
 * Visibility is permission-driven (Communications.WhatsApp.*). Admins bypass
 * the checks (non-strict), so they always see every item.
 *
 * Modelled on `appCatalogueMenu.tsx`.
 */

import {
  IconLayoutDashboard,
  IconPencil,
  IconUsersGroup,
  IconSend,
  IconInbox,
  IconTemplate,
  IconAddressBook,
  IconActivity,
  IconSettings,
  IconRocket,
  IconSpeakerphone,
  IconReceipt2,
} from '@tabler/icons-react';
import { DomainMenuProvider, MenuSection, DomainMenuConfig } from '../../layouts/menu/types';
import { createVisibilityCheck } from './BaseMenuProvider';
import { PERMISSIONS } from '../../config/permissions';

const P = PERMISSIONS.COMMUNICATIONS.WHATSAPP;
const BASE = '/admin/communications/whatsapp';

class CommsWhatsAppMenuProvider implements DomainMenuProvider {
  domainId = 'comms-whatsapp';

  getMenuConfig(): DomainMenuConfig {
    const view = createVisibilityCheck({ permissions: [P.VIEW] });
    const send = createVisibilityCheck({ permissions: [P.SEND] });
    const manage = createVisibilityCheck({ permissions: [P.MANAGE] });
    const settings = createVisibilityCheck({ permissions: [P.SETTINGS] });
    const campaigns = createVisibilityCheck({ permissions: [PERMISSIONS.COMMUNICATIONS.CAMPAIGNS.VIEW] });

    const sections: MenuSection[] = [
      {
        id: 'comms-whatsapp-overview',
        label: 'Overview',
        visible: view,
        items: [
          {
            id: 'comms-whatsapp-dashboard',
            label: 'Dashboard',
            path: BASE,
            icon: <IconLayoutDashboard size={16} />,
            visible: view,
          },
        ],
      },
      {
        id: 'comms-whatsapp-send',
        label: 'Send',
        visible: send,
        items: [
          {
            id: 'comms-whatsapp-compose',
            label: 'Compose',
            path: `${BASE}/compose`,
            icon: <IconPencil size={16} />,
            visible: send,
          },
          {
            id: 'comms-whatsapp-bulk',
            label: 'Bulk Send',
            path: `${BASE}/bulk`,
            icon: <IconUsersGroup size={16} />,
            visible: send,
          },
          {
            id: 'comms-whatsapp-flows',
            label: 'Flows',
            path: `${BASE}/flows`,
            icon: <IconRocket size={16} />,
            visible: send,
          },
        ],
      },
      {
        id: 'comms-whatsapp-messages',
        label: 'Messages',
        visible: view,
        items: [
          {
            id: 'comms-whatsapp-outbox',
            label: 'Outbox',
            path: `${BASE}/outbox`,
            icon: <IconSend size={16} />,
            visible: view,
          },
          {
            id: 'comms-whatsapp-inbox',
            label: 'Inbox',
            path: `${BASE}/inbox`,
            icon: <IconInbox size={16} />,
            visible: view,
          },
          {
            id: 'comms-whatsapp-campaigns',
            label: 'Campaigns',
            path: `${BASE}/campaigns`,
            icon: <IconSpeakerphone size={16} />,
            visible: campaigns,
          },
        ],
      },
      {
        id: 'comms-whatsapp-library',
        label: 'Library',
        visible: manage,
        items: [
          {
            id: 'comms-whatsapp-templates',
            label: 'Templates',
            path: `${BASE}/templates`,
            icon: <IconTemplate size={16} />,
            visible: manage,
          },
        ],
      },
      {
        id: 'comms-whatsapp-account',
        label: 'Account',
        visible: view,
        items: [
          {
            id: 'comms-whatsapp-profile',
            label: 'Profile',
            path: `${BASE}/profile`,
            icon: <IconAddressBook size={16} />,
            visible: manage,
          },
          {
            id: 'comms-whatsapp-diagnostics',
            label: 'Diagnostics',
            path: `${BASE}/diagnostics`,
            icon: <IconActivity size={16} />,
            visible: view,
          },
          {
            id: 'comms-whatsapp-billing',
            label: 'Billing',
            path: `${BASE}/billing`,
            icon: <IconReceipt2 size={16} />,
            visible: view,
          },
        ],
      },
      {
        id: 'comms-whatsapp-config',
        label: 'Configuration',
        visible: settings,
        items: [
          {
            id: 'comms-whatsapp-settings',
            label: 'Settings',
            path: `${BASE}/settings`,
            icon: <IconSettings size={16} />,
            visible: settings,
          },
        ],
      },
    ];

    return {
      domainId: this.domainId,
      sections,
      metadata: {
        version: '1.0.0',
        lastUpdated: Date.now(),
        description: 'WhatsApp channel — admin console',
      },
    };
  }
}

export const commsWhatsAppMenuProvider = new CommsWhatsAppMenuProvider();
